import angular from 'angular';
import uiBootstrap from 'angular-ui-bootstrap';
import csmEvents from '../events/events';

export default angular
  .module('csm.ui.lazyTypeahead', [uiBootstrap, csmEvents])

  /** Constant popup path. */
  .constant(
    'LAZY_TYPEAHEAD_POPUP_PATH',
    'templates/lazyTypeahead.template.html'
  )

  /** Popup Owner Class Constant. */
  .constant('LAZY_TYPEAHEAD_POPUP_OWNER', 'lazyTypeaheadPopupOwner')

  /*******************************************************************************
   * lazyTypeahead */
  /**
   * @ngdoc    directive
   * @restrict E
   * @module   csm.ui
   * @name     csm.ui.lazyTypeahead.directive:lazyTypeahead
   *
   * @scope
   * @param {expression} dataprovider      Evaluates to a promise or an array to use
   *                                       as the source list to check. $viewValue = input
   * @param {expression} labelExpression   Evaluates to a string. $data = Current list object
   * @param {expression} ltOnBlur          Called on blur from input. $event = Blur event
   * @param {expression} ltOnEnter         Called on enter key from input. $event = Keydown event
   * @param {expression} ltOnFocus         Called on focus of input. $event = Focus event
   * @param {expression} modelLoad         Evaluates to a promise or an object which is used
   *                                       with the internal model tracking. $model = Model value
   * @param {String}     placeholder       Pass-through to input attribute
   * @param {expression} reqSelection      Evaluates to a truthy expression to indicate that a
   *                                       match must exist. If true and no match on a commit, then
   *                                       the input/model is cleared
   * @param {expression} selectionChanged  Evaluated when the internal selection model has changed via
   *                                       user interaction or when the ngModel has been programmatically
   *                                       changed from outside the directive. $current = Current value,
   *                                       $previous = Previous selection model
   * @param {String}     valueField        Optional field to get model value from. When not supplied,
   *                                       the entire dp list item is used.
   * @param {String}     page-size         Optional count to load when lazy scrolling. Default=10
   * @param {Attribute}  containsText      Optional attribute that when present indicates the
   *                                       entered text will search any portion of the label.
   *
   * @description
   * This is a wrapper for the uibTypeahead component that will utilize the first
   * letter in the input for the data query and then store the results and use
   * them to filter for any additional input characters. This will utilize the
   * lazyTypeaheadPopup directive to load matches by a specified page-size as
   * needed.
   *
   * When the ngModel value is changed from outside this directive, the modelLoad
   * expression is executed to retrieve the corresponding data model when appropriate.
   * The result of the expression can be a promise for which this component will
   * wait before setting any internal model values.
   *******************************************************************************/
  /* @ngInject */
  .directive('lazyTypeahead', function (LAZY_TYPEAHEAD_POPUP_PATH) {
    /*----------------------------------------*/
    /* Add style rule to ensure block display */
    /* so border styling will look correct    */
    /*----------------------------------------*/
    angular
      .element(document.querySelector('body'))
      .append(
        angular.element('<style> lazy-typeahead { display: block } </style>')
      );

    return {
      restrict: 'E',
      require: ['ngModel', 'lazyTypeahead'],
      scope: {
        dataprovider: '&', // Provided $viewValue
        labelExpression: '&', // Provided $data
        searchTargets: '&', // returns string or array of strings
        ltOnBlur: '&', // Provided $event
        ltOnEnter: '&', // Provided $event
        ltOnFocus: '&', // Provided $event
        modelLoad: '&', // Provided $model
        placeholder: '@',
        reqSelection: '&',
        selectionChanged: '&', // Provided $current and $previous
        valueField: '@'
      },
      /*-------------------------------------------*/
      /* Use the template function option in order */
      /* to allow for attribute pass-throughs from */
      /* the lazyTypeahead to the uibTypeahead.    */
      /*-------------------------------------------*/
      template: function (element, attrs) {
        return (
          '\
        <input ' +
          (attrs.appendToBody
            ? 'typeahead-append-to-body = "' + attrs.appendToBody
            : '') +
          '"\
               ng-model                     = "ctrl.model"\
               uib-typeahead                = "data as ctrl.getLabel(data) for data in ctrl.getDP($viewValue)"\
               typeahead-on-select          = "ctrl.handleSelect()"\
               typeahead-select-on-blur     = "true"\
               typeahead-focus-first        = "true"\
               class                        = "form-control"\
               ng-disabled                  = "ctrl.isDisabled"\
               typeahead-popup-template-url = "' +
          LAZY_TYPEAHEAD_POPUP_PATH +
          '"\
               typeahead-input-formatter    = "ctrl.getLabel($model)"\
               ng-focus                     = "ctrl.handleFocus($event)"\
               ng-blur                      = "ctrl.handleBlur($event)"\
               csm-enter                    = "ctrl.handleEnter($event)"/>'
        );
      },
      link: function (element, scope, attr, ctrl) {
        ctrl[1].init(ctrl[0]);
      },
      controllerAs: 'ctrl',
      controller: function ($scope, $element, $q, $timeout) {
        /*************************************************************************
         * Properties
         *************************************************************************/
        /** Controller Instance. */ var mCtrl = this;
        /** Cached input character used for cache update checks. */ var mCachedInputChar =
          null;
        /** Cached dataprovider to filter against. */ var mCachedDP = null;
        /** Search targets attr present flag. */ var mHasSearchTargets =
          $element.attr('search-targets') != undefined;
        /** Input text matches any portion of label. */ var mDoContains =
          $element.attr('contains-text') != undefined;
        /** Typeahead Input. */ var mInput = null;
        /** Dataprovider Loading Promise. */ var mLoadingPromise = null;
        /** ngModel Controller Reference. */ var mNgModelCtrl = null;
        /** Previous controller selection model. */ var mPreviousModel = null;
        /** Processing blur event flag. */ var mProcessingBlur = false;

        /*---------------------------*/
        /* Set controller functions. */
        /*---------------------------*/
        mCtrl.addEventListener = addEventListener;
        mCtrl.clear = clear;
        mCtrl.getDP = getDP;
        mCtrl.getLabel = getLabel;
        mCtrl.handleBlur = handleBlur;
        mCtrl.handleEnter = handleEnter;
        mCtrl.handleFocus = handleFocus;
        mCtrl.handleSelect = handleSelect;
        mCtrl.init = init;

        /*-------------------------------------------------------*/
        /* Set up a watch for when this element becomes disabled */
        /*-------------------------------------------------------*/
        $scope.$watch(
          function () {
            return $element.attr('disabled');
          },
          function (disabled) {
            mCtrl.isDisabled = !!disabled;
          }
        );

        /*************************************************************************
         * addEventListener */
        /**
         * @ngdoc    function
         * @name     addEventListener
         * @methodOf csm.ui.lazyTypeahead.directive:lazyTypeahead
         *
         * @param {String}   name    Event type to listen for
         * @param {function} handler Handler function call when the event occurs
         *
         * @description
         * Adds an event handler from another scope to the events being fired on
         * this scope.
         *************************************************************************/
        function addEventListener(name, handler) {
          $scope.$on(name, handler);
        }

        /***
         * Dismiss the typeahead dropdown. uibTypeahead listens for Escape on the input and responds by
         * emptying its internal matches array, which causes lazyTypeaheadPopup's `matches.length` watcher
         * to remove the `show` class. We need to do this manually because a scanner-resolved match never
         * triggers uibTypeahead's own selection flow, so its result list would otherwise stay visible.
         **/
        function clear() {
          mCachedInputChar = null;
          mCachedDP = null;
          if (mInput && mInput[0]) {
            $timeout(function () {
              mInput[0].dispatchEvent(
                new KeyboardEvent('keydown', {
                  keyCode: 27,
                  which: 27,
                  key: 'Escape',
                  bubbles: true
                })
              );
            }, 0);
          }
        }

        /*************************************************************************
         * commitSelectionModel */
        /**
         * This is called when the controller selection model is to be set. The
         * previous model value is stored and the selectionChanged expression
         * is executed.
         *
         * param model  Selection model to commit.
         *************************************************************************/
        function commitSelectionModel(model) {
          $scope.selectionChanged({
            $current: (mCtrl.model = model),
            $previous: mPreviousModel
          });
          mPreviousModel = mCtrl.model;
        }

        /*************************************************************************
         * dpModelIsObject */
        /**
         * Indication that the dataprovider consists of objects as a valueField has
         * been specified
         *************************************************************************/
        function dpModelIsObject() {
          return !!$scope.valueField;
        }

        /*************************************************************************
         * filterDP */
        /**
         * Filter the cached dataprovider to find all items whose searchable text
         * matches the provided input. By default, only the display label is
         * searchable. When the consumer provides a `search-targets` expression,
         * each item can expose multiple searchable strings (e.g., name, fullName,
         * studentID) so the input can match any of them while the displayed label
         * remains unchanged.
         *
         * Matching honors the `contains-text` attribute: when present, the input
         * may appear anywhere in a target; otherwise the target must start with
         * the input.
         *
         * param  input  Value to filter on.
         *************************************************************************/
        function filterDP(input) {
          /*------------------------------------------------------------------*/
          /* Normalize the input once for case-insensitive comparison and to  */
          /* avoid recomputing toLowerCase()/trim() inside the filter loop.   */
          /*------------------------------------------------------------------*/
          var lcInput = (input || '').toLowerCase().trim();

          return (
            (mCachedDP || [])
              .filter(function (item) {
                /*--------------------------------------------------------------*/
                /* Build the list of strings to test against for this item.     */
                /* When `search-targets` is provided on the directive, the      */
                /* expression is evaluated to produce one or more searchable    */
                /* strings. Otherwise, fall back to the displayed label so      */
                /* existing usages behave exactly as before.                    */
                /*--------------------------------------------------------------*/
                var targets;

                if (mHasSearchTargets) {
                  targets = $scope.searchTargets({ $data: item });

                  /*------------------------------------------------------------*/
                  /* Normalize the result so downstream code can always treat   */
                  /* `targets` as an array. Null/undefined becomes an empty     */
                  /* array (no match), and a single string is wrapped.          */
                  /*------------------------------------------------------------*/
                  if (targets == null) targets = [];
                  if (!angular.isArray(targets)) targets = [targets];
                } else {
                  targets = [mCtrl.getLabel(item)];
                }

                /*--------------------------------------------------------------*/
                /* Item is a match if ANY of its targets matches the input.     */
                /* `mDoContains` (driven by the `contains-text` attribute)      */
                /* controls whether we check substring-anywhere (idx > -1) or   */
                /* starts-with (idx === 0). Coerce each target to a string and  */
                /* guard against null so numeric IDs and missing fields don't   */
                /* throw inside indexOf.                                        */
                /*--------------------------------------------------------------*/
                return targets.some(function (t) {
                  var idx = ((t == null ? '' : t) + '')
                    .toLowerCase()
                    .indexOf(lcInput);
                  return mDoContains ? idx > -1 : idx === 0;
                });
              })

              /*----------------------------------------------------------------*/
              /* Sort the matches alphabetically by their displayed label so    */
              /* the popup ordering is predictable regardless of which target   */
              /* field caused the match.                                        */
              /*----------------------------------------------------------------*/
              .sort(function (item1, item2) {
                return mCtrl
                  .getLabel(item1)
                  .toLowerCase()
                  .localeCompare(mCtrl.getLabel(item2).toLowerCase());
              })
          );
        }

        /*************************************************************************
         * getDP */
        /**
         * This is used by the uibTypeahead to retrieve the list of matches
         * for the provided input. If there is already a loading promise in
         * progress, this will return a promise that resolves when that load is
         * finished. This is for the case when sequential key inputs have triggered
         * this method and the first request has not received a response.
         *
         * param  input  Input string to filter against
         * return        Filtered array when cache is not needed to be updated or
         *                a promise that will update the cache and then return
         *                the filtered list
         *************************************************************************/
        function getDP(input) {
          if (mLoadingPromise)
            return mLoadingPromise.then(function () {
              return loadDP(input);
            });
          else return loadDP(input);
        }

        /*************************************************************************
         * getLabel */
        /**
         * Returns the string representation to display of the provided data item
         * using the labelExpression. Any new lines, line feeds, and extra space
         * are removed.
         *
         * param  data  Object to be formatted for display.
         *************************************************************************/
        function getLabel(data) {
          return ($scope.labelExpression({ $data: data }) || data || '')
            .toString()
            .replace(/\r?\n|\r/g, '')
            .trim();
        }

        /*************************************************************************
         * handleBlur */
        /**
         * @ngdoc    function
         * @name     handleBlur
         * @methodOf csm.ui.lazyTypeahead.directive:lazyTypeahead
         *
         * @param {object} event Blur event object being handled. This will be provided
         *                       to the **ltOnBlur** scope expression as *$event*
         *
         * @description
         * Input field blur handler. A timeout is used to see if a match was
         * clicked which triggered a blur, however the uibTypeahead will put focus
         * back on the input so the blur should only be propagated if the
         * activeElement isn't the input. When the focus actually changes, then the
         * directive model is updated.
         *************************************************************************/
        function handleBlur(event) {
          mProcessingBlur = true;

          $timeout(function () {
            if (document.activeElement != mInput[0]) {
              updateModel();
              $scope.ltOnBlur({ $event: event });
              $scope.$broadcast('ltOnBlur');
            }

            mProcessingBlur = false;
          });
        }

        /*************************************************************************
         * handleEnter */
        /**
         * @ngdoc    function
         * @name     handleEnter
         * @methodOf csm.ui.lazyTypeahead.directive:lazyTypeahead
         *
         * @param {object} event Enter event object being handled. This will be provided
         *                       to the **ltOnEnter** scope expression as *$event*
         *
         * @description
         * Enter key handler. Commit the current typeahead model to the directive
         * model and notify the listener
         *************************************************************************/
        function handleEnter(event) {
          updateModel();
          $scope.ltOnEnter({ $event: event });
          $scope.$broadcast('ltOnEnter');
        }

        /*************************************************************************
         * handleFocus */
        /**
         * @ngdoc    function
         * @name     handleFocus
         * @methodOf csm.ui.lazyTypeahead.directive:lazyTypeahead
         *
         * @param {object} event Focus event object being handled. This will be provided
         *                       to the **ltOnBlur** scope expression as *$event*
         *
         * @description
         * Notify listener of input receiving focus. If a focus is received while
         * processing a blur then no notification is made as the input will still
         * have the focus.
         *************************************************************************/
        function handleFocus(event) {
          if (mProcessingBlur) return;

          $scope.ltOnFocus({ $event: event });
          $scope.$broadcast('ltOnFocus');
        }

        /*************************************************************************
         * handleSelect */
        /**
         * @ngdoc    function
         * @name     handleSelect
         * @methodOf csm.ui.lazyTypeahead.directive:lazyTypeahead
         *
         * @description
         * Match selection handler (mouse click, enter key, or select-on-blur).
         * A selection is a definitive choice, so commit it to the directive
         * model right away instead of waiting for a blur. The blur commit
         * cannot be relied on here: uibTypeahead puts focus back on the input
         * after a selection, which makes handleBlur's focus check skip the
         * commit (ex. selecting via select-on-blur by clicking a search
         * button would otherwise search with the previous model value).
         *************************************************************************/
        function handleSelect() {
          updateModel();
        }

        /*************************************************************************
         * init */
        /**
         * Initialization function to be called by the link method of this directive
         * so the ngModel Controller reference can be passed.
         *
         * param  ngModelCtrl  Controller object to save.
         *************************************************************************/
        function init(ngModelCtrl) {
          mNgModelCtrl = ngModelCtrl;

          mInput = $element.find('input');

          /*------------------------------------*/
          /* Set up a watch for the placeholder */
          /* attribute to be set on the input   */
          /*------------------------------------*/
          $scope.$watch('placeholder', function (placeholder) {
            mInput.attr('placeholder', placeholder);
          });

          /*----------------------------------------------------------------*/
          /* Set up a focus/blur function that puts focus/blur on the input */
          /*----------------------------------------------------------------*/
          $element[0].focus = function () {
            mInput[0].focus();
          };
          $element[0].blur = function () {
            mInput[0].blur();
          };

          /*---------------------------------------------------------------------------------*/
          /* Add a formatter to retrieve the model object using the modelLoad expression. If */
          /* nothing is found with the modelLoad expression, the dataprovider expression is  */
          /* used to retrieve a list and then a filter is applied to match the model value   */
          /*                                                                                 */
          /* Note: This formatter does not change the passed value as we are using this      */
          /* formatter method as a way to be notified when the model value changes from      */
          /* outside the directive.                                                          */
          /*---------------------------------------------------------------------------------*/
          mNgModelCtrl.$formatters.unshift(function (value) {
            /*-----------------------------------------------------------*/
            /* Use promise chaining to attempt to load the model object. */
            /* The modelLoad expression will be passed the model value   */
            /* as $model in attempts to retrieve the model object.       */
            /*-----------------------------------------------------------*/
            $q.when($scope.modelLoad({ $model: value }))
              .then(function (model) {
                /*---------------------------------------------------------*/
                /* If no model is returned, the dataprovider expression is */
                /* utilized as an attempt to retrieve the whole list and   */
                /* filtering is done using the valueField if specified.    */
                /*---------------------------------------------------------*/
                if (!model)
                  return $q.when($scope.dataprovider()).then(function (dp) {
                    return (dp || [])
                      .filter(function (data) {
                        return dpModelIsObject()
                          ? data[$scope.valueField] == value
                          : data == value;
                      })
                      .shift();
                  });
                /*------------------------------------------*/
                /* Otherwise the model was found, so return */
                /* it so the next promise can process it    */
                /*------------------------------------------*/ else
                  return model;
              })

              /*-------------------------------------------*/
              /* This promise will process any model value */
              /* to set and clear any caching properties   */
              /*-------------------------------------------*/
              .then(function (modelToSet) {
                mCachedInputChar = null;
                mCachedDP = null;

                /*---------------------------------------------------*/
                /* Commit the loaded selection model. A timeout is   */
                /* used so an infinite digest cycle is not generated */
                /*---------------------------------------------------*/
                $timeout(function () {
                  /*--------------------------------------------------------------*/
                  /* If modelToSet has been found and there is no specified       */
                  /* valueField. This indicates the objects in the dataprovider   */
                  /* will be used as the directive model. When this is the case,  */
                  /* we need to ensure the directive model is the same as the     */
                  /* modelToSet which will have * the expected object definition. */
                  /*--------------------------------------------------------------*/
                  if (
                    modelToSet &&
                    !$scope.valueField &&
                    !angular.equals(value, modelToSet)
                  ) {
                    mCtrl.model = modelToSet;
                    updateModel();
                  } else
                    commitSelectionModel(
                      modelToSet || (selectionRequired() ? null : value)
                    );

                  /*-----------------------------------------------*/
                  /* Just update the internal tracking model value */
                  /*-----------------------------------------------*/
                });
              });

            return value;
          });
        }

        /*************************************************************************
         * loadDP */
        /**
         * This is used by the uibTypeahead to retrieve the list of matches
         * for the provided input. The input is checked against the cached input
         * character to determine if the cached list needs to be updated. If not,
         * the cached dataprovider is filtered against the input string and returned.
         * Otherwise the dataprovider expression is evaluated to update the cached list
         * before filtering is applied and the result is returned.
         *
         * param  input Input string to filter against
         * return       Filtered array when cache is not needed to be updated or
         *               a promise that will update the cache and then return
         *               the filtered list
         *************************************************************************/
        function loadDP(input) {
          /*-------------------------------------------------------------------*/
          /* Check for a change in the input indicated by the input not        */
          /* starting with the cached input or the cached input being not set. */
          /* This indicates that the cached dataprovider needs to be updated.  */
          /*                                                                   */
          /* Note: The input to check will be set to all lowercase for         */
          /* case-insensitive comparison                                       */
          /*-------------------------------------------------------------------*/
          var lcInput = (input || '').toLowerCase();

          if (!mCachedInputChar || lcInput.indexOf(mCachedInputChar) != 0) {
            return (mLoadingPromise = $q
              .when($scope.dataprovider({ $viewValue: input }))
              .then(function (dp) {
                mCachedInputChar = lcInput.charAt(0);
                mCachedDP = dp || [];
                return filterDP(input);
              })
              ['finally'](function () {
                mLoadingPromise = null;
              }));
          } else return filterDP(input);

          /*----------------------------------------------------------------*/
          /* Otherwise the cached dp can be used directly to filter against */
          /*----------------------------------------------------------------*/
        }

        /*************************************************************************
         * selectionRequired */
        /**
         * Sugar for converting truthy reqSelection expression to boolean value.
         *************************************************************************/
        function selectionRequired() {
          return !!$scope.reqSelection();
        }

        /*************************************************************************
         * updateModel */
        /**
         * Update the directive model from the controller model.
         *************************************************************************/
        function updateModel() {
          /*------------------------------------------*/
          /* Clear the directive model if a selection */
          /* is required and there is no match        */
          /*------------------------------------------*/
          if (
            angular.isString(mCtrl.model) &&
            dpModelIsObject() &&
            selectionRequired()
          ) {
            mNgModelCtrl.$setViewValue(null);
            commitSelectionModel(null);
          } else {
            /*-------------------------------------------------------*/
            /* Otherwise update the directive model to the           */
            /* valueField if its an object or use the model directly */
            /*-------------------------------------------------------*/
            if (angular.isObject(mCtrl.model) && dpModelIsObject())
              mNgModelCtrl.$setViewValue(mCtrl.model[$scope.valueField]);
            /*---------------------------------------------*/
            /* When the selection isn't required the model */
            /* can be updated with the non-matched text    */
            /*---------------------------------------------*/ else
              mNgModelCtrl.$setViewValue(mCtrl.model);

            /*---------------------------------------*/
            /* The model hasn't changed we just      */
            /* want to confirm the current selection */
            /*---------------------------------------*/
            commitSelectionModel(mCtrl.model);
          }
        }
      }
    };
  })

  /*******************************************************************************
   * lazyTypeaheadPopup */
  /**
   * This directive is meant to be used in the popup template associated with the
   * uibTypeaheadPopup directive. It will share the same scope so it can access the
   * scope properties like the array of matches. However this will load matches as
   * needed by an indicated page size (default=10) from the lazyTypeahead parent
   * directive element.
   *
   * Matches will be loaded when scrolling is attempted on the internal ul element
   * and when the matches are initially shown. To always ensure some initial scrolling
   * is available, the height of the list considers the number of displayed items to
   * be the page size minus one.
   *
   * Keyboard support has been added so that the active match is always in view.
   *
   * ngdoc     directive
   * memberof  csm.ui.lazyTypeahead
   * name      csm.ui.lazyTypeahead.directive:lazyTypeaheadPopup
   *******************************************************************************/
  .directive(
    'lazyTypeaheadPopup',
    function ($compile, $timeout, LAZY_TYPEAHEAD_POPUP_OWNER) {
      return (
        0,
        {
          restrict: 'E',
          template: '<ul class="dropdown-menu" style="overflow-y: auto"></ul>',
          link: function (scope, element) {
            /** Parent Div for this directive in the LAZY_TYPEAHEAD_POPUP_PATH template.
          This is found by navigating the ancenstry tree of the element and looking
          for the LAZY_TYPEAHEAD_POPUP_OWNER. */
            var parentDiv = null;

            do {
              parentDiv = element.parent();
            } while (
              parentDiv[0].tagName != 'BODY' &&
              !parentDiv.hasClass(LAZY_TYPEAHEAD_POPUP_OWNER)
            );

            /*-----------------------------------------------*/
            /* The parentDiv will contain the unique id      */
            /* that the uibTypeahead instance will reference */
            /*-----------------------------------------------*/
            /** Uib Typeahead. */ var mParentTypeahead = angular.element(
              document.querySelector('[aria-owns=' + parentDiv.attr('id') + ']')
            );
            /** Lazy Typeahead. */ var mParentLazyTypeahead =
              mParentTypeahead.parent();
            /** Directive UL element. */ var mList = element.find('ul');

            /*-------------------*/
            /* Paging Properties */
            /*-------------------*/
            /** Current loaded page. */ var mCurrentPage = 0;
            /** Page size. Default=10. */ var mPageSize =
              Math.max(1, Number(mParentLazyTypeahead.attr('page-size'))) || 10;
            /** Item List scope. */ var mItemScope = null;

            /*************************************************************************
             * getAbsoluteListHeight */
            /**
             * Returns the number of pixels for the list height including the top and
             * bottom padding.
             *
             * return Numeric pixel amount
             *************************************************************************/
            function getAbsoluteListHeight() {
              var style = window.getComputedStyle(mList[0], null);

              return (
                getStyleNumProp(style, 'height') +
                getStyleNumProp(style, 'padding-top') +
                getStyleNumProp(style, 'padding-bottom')
              );
            }

            /*************************************************************************
             * getStyleNumProp */
            /**
             * Retrieves the property from the provided style object and returns the
             * parsed numeric value.
             *
             * param  style  Style object from window.getComputedStyle
             * param  prop   Property name to retrieve the value for
             * return        Numeric value of the style property
             *************************************************************************/
            function getStyleNumProp(style, prop) {
              return parseInt(style.getPropertyValue(prop));
            }

            /*************************************************************************
             * showNextPage */
            /**
             * Loads the next batch of matches into the DOM for the current page. This
             * is called when the scrolling has reached the bottom of the list or the
             * list of matches has changed.
             *************************************************************************/
            function showNextPage() {
              /*---------------------------------*/
              /* Do nothing if all data is shown */
              /*---------------------------------*/
              var startIdx = mCurrentPage * mPageSize;
              if (startIdx >= scope.matches.length) return;

              /*--------------------------------*/
              /* Next batch of items to display */
              /*--------------------------------*/
              var items = scope.matches.slice(startIdx, startIdx + mPageSize);

              /*-------------------------------------------*/
              /* Create list items that utilize the        */
              /* typeahead default attributes for behavior */
              /*-------------------------------------------*/
              for (var i = 0; i < items.length; i++) {
                var matchIndex = startIdx + i;
                var li = angular.element('<li>');
                li.addClass('uib-typeahead-match');
                li.attr({
                  'ng-class': '{active: isActive(' + matchIndex + ') }',
                  'ng-mouseenter': 'selectActive(' + matchIndex + ')',
                  'ng-click': 'selectMatch(' + matchIndex + ', $event)',
                  role: 'option',
                  id: '::' + items[i].id
                });

                var div = angular.element('<div uib-typeahead-match></div>');
                div.attr({
                  index: matchIndex,
                  match: 'matches[' + matchIndex + ']',
                  query: 'query',
                  'template-url': 'templateUrl'
                });

                li.append(div);
                mList.append($compile(li)(mItemScope));
              }

              /*-----------------*/
              /* Update the page */
              /*-----------------*/
              mCurrentPage++;
            }

            /*---------------------------------------------------*/
            /* Watch for the active index to change and ensure   */
            /* the active element is within the scrolling window */
            /*---------------------------------------------------*/
            scope.$watch('active', function (activeIdx) {
              /*-----------------------------------*/
              /* No matches to display, do nothing */
              /*-----------------------------------*/
              if (!scope.matches.length) return;

              /*----------------------------------------------------------------*/
              /* Prevent setting an index that hasn't been loaded into the DOM. */
              /*----------------------------------------------------------------*/
              if (activeIdx > mList.children().length - 1) {
                scope.active = 0;
                return;
              }

              /*---------------------------*/
              /* Get viewable pixel bounds */
              /*---------------------------*/
              var listHeight = getAbsoluteListHeight();
              var minViewScroll = mList[0].scrollTop;
              var maxViewScroll = listHeight + mList[0].scrollTop;

              /*--------------------------------------------------*/
              /* Verify active element is viewable within the DIV */
              /*--------------------------------------------------*/
              var activeElement = mList[0].children[activeIdx];
              var elementHeight = activeElement.clientHeight;

              var isAboveView = activeElement.offsetTop < minViewScroll;
              var isBelowView =
                activeElement.offsetTop + elementHeight >= maxViewScroll;

              if (!isAboveView && !isBelowView) return;

              /*-------------------------------------------*/
              /* When the element is above the viewable    */
              /* content, scroll it to the top of the list */
              /*-------------------------------------------*/
              if (isAboveView) mList[0].scrollTop = activeElement.offsetTop;
              /*-----------------------------------------------------*/
              /* Otherwise scroll the list so the active element is  */
              /* at the bottom. This is done by taking the element's */
              /* offset top amount and subtracting the height of the */
              /* elements that would be displayed before it          */
              /*-----------------------------------------------------*/ else {
                var newScroll =
                  activeElement.offsetTop - elementHeight * (mPageSize - 2);
                mList[0].scrollTop = newScroll;
              }
            });

            /*------------------------------------------------------------*/
            /* The matches array is never re-instantiated, so we watch    */
            /* the length to know when the matches have changed. On the   */
            /* change we clear out the previous list items and begin anew */
            /*------------------------------------------------------------*/
            scope.$watch('matches.length', function (length) {
              /*----------------------------------------------------*/
              /* Add/remove show class. This has to be done here to */
              /* ensure the list is visible for sizing calculations */
              /*----------------------------------------------------*/
              if (length) mList.addClass('show');
              else mList.removeClass('show');

              /*------------------------------*/
              /* Clean up the old item scope. */
              /*------------------------------*/
              if (mItemScope) mItemScope.$destroy();

              /*--------------------------------------*/
              /* Reset the list and paging properties */
              /*--------------------------------------*/
              mList.empty();
              mList.css('max-height', '');
              mCurrentPage = 0;
              mItemScope = scope.$new(false);

              /*---------------------*/
              /* Load the first page */
              /*---------------------*/
              showNextPage();

              /*------------------------------------------------------------*/
              /* Ensure the max-height property is set. This is done with a */
              /* timeout as scope.isOpen() looks at the available matches   */
              /* and popup has not been displayed during this digest cycle. */
              /*                                                            */
              /* The css value can be either "none" (JQuery) or ""(JQLite). */
              /*------------------------------------------------------------*/
              if (
                scope.isOpen() &&
                ['', 'none'].indexOf(mList.css('max-height')) > -1
              )
                $timeout(function () {
                  /*----------------------------------------------------*/
                  /* Use the first list item to establish a height base */
                  /*----------------------------------------------------*/
                  var child = Array.prototype.slice
                    .apply(mList[0].children)
                    .shift();

                  if (!child) return;

                  /*-----------------------------------------------*/
                  /* Since we load on a scroll the max-height will */
                  /* be accounting for pageSize minus item heights */
                  /*-----------------------------------------------*/
                  var singleHeight = child.clientHeight;
                  mList.css(
                    'max-height',
                    singleHeight * (mPageSize - 1) + 'px'
                  );
                });
            });

            /*---------------------------------------------*/
            /* Add a scroll event listener that will load  */
            /* the next page of results when the scrolling */
            /* will exceed reach the bottom of the list    */
            /*---------------------------------------------*/
            mList.on('scroll', function () {
              var height = getAbsoluteListHeight();

              if (mList[0].scrollTop + height >= mList[0].scrollHeight)
                showNextPage();
            });
          }
        }
      );
    }
  )

  /** Add a template to the cache that utilizes the lazyTypeaheadPopup directive. This
    uses logic that the uibTypeahead default popup templated used. */
  .run(
    function (
      $templateCache,
      LAZY_TYPEAHEAD_POPUP_PATH,
      LAZY_TYPEAHEAD_POPUP_OWNER
    ) {
      $templateCache.put(
        LAZY_TYPEAHEAD_POPUP_PATH,
        '<div style="position:absolute" class="' +
          LAZY_TYPEAHEAD_POPUP_OWNER +
          '"' +
          ' ng-show="isOpen() && !moveInProgress"' +
          " ng-style=\"{top: position().top+'px', left: position().left+'px'}\"" +
          ' role="listbox" aria-hidden="{{!isOpen()}}">' +
          '<lazy-typeahead-popup></lazy-typeahead-popup>' +
          '</div>'
      );
    }
  ).name;
